<template>
  <a-form-model
    layout="horizontal"
    :model="shiftForm"
    :rules="rules"
    :validateOnRuleChange="true"
    ref="shiftFormRef"
    class="flex flex-column gap-2"
    :label-col="{ span: 6 }"
    :wrapper-col="{ span: 18 }"
  >
    <div class="flex flex-column gap-2">
      <div class="flex justify-content-between">
        <h3>Work Time</h3>
        <a-button @click="clearSignedTime">Clear</a-button>
      </div>
      <a-form-model-item :colon="false" label-align="left" label="Start" prop="work_start">
        <a-date-picker
          v-model="shiftForm.work_start"
          placeholder="Select Start"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled-time="() => false"
          :disabled-date="date => disabledSignedStartDate(date, !isNew && !shiftForm.set_scheduled_as_signed)"
          @change="handleChangeSignedStart"
        />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="End" prop="work_end">
        <a-date-picker
          v-model="shiftForm.work_end"
          placeholder="Select End"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled="!shiftForm.work_start"
          :disabled-time="() => false"
          :disabled-date="date => disabledSignedStartDate(date, !isNew && !shiftForm.set_scheduled_as_signed)"
          @change="handleChangeSignedEnd"
        />
      </a-form-model-item>
    </div>

    <div class="flex flex-column gap-2">
      <div class="flex justify-content-between">
        <h3>1st Meal Break Time</h3>
        <a-button @click="clearLunch">Clear</a-button>
      </div>
      <a-form-model-item :colon="false" label-align="left" label="Start" prop="lunch_start">
        <a-date-picker
          v-model="shiftForm.lunch_start"
          placeholder="Select Start"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled="isLunchDisabled"
          :disabled-time="disabledLunchStartTime"
          :disabled-date="disabledLunchStartDate"
          @change="handleChangeLunchStart"
          @openChange="handleOpenChangeLunchStart"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="End" prop="lunch_end">
        <a-date-picker
          v-model="shiftForm.lunch_end"
          placeholder="Select End"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled="isLunchEndDisabled"
          :disabled-time="disabledLunchEndTime"
          :disabled-date="disabledLunchEndDate"
          @change="handleChangeLunchEnd"
          @openChange="handleOpenChangeLunchEnd"
        />
      </a-form-model-item>

      <a-alert type="info" show-icon v-for="message in lunchWaiveInfo" :key="message" :message="message" />
      <a-form-model-item
        :colon="false"
        label-align="left"
        label="Waive Meal Break"
        :label-col="{ span: 20 }"
        :wrapper-col="{ span: 4 }"
      >
        <div class="flex justify-content-end">
          <a-switch v-model="isLunchWaived" :disabled="!isLunchWaiveAvailable || !isLunchWaiver" />
        </div>
      </a-form-model-item>
    </div>

    <div class="flex flex-column gap-2">
      <div class="flex justify-content-between">
        <h3>2nd Meal Break Time</h3>
        <a-button @click="clearSecondLunch">Clear</a-button>
      </div>
      <a-form-model-item :colon="false" label-align="left" label="Start" prop="second_lunch_start">
        <a-date-picker
          v-model="shiftForm.second_lunch_start"
          placeholder="Select Start"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled="isSecondLunchDisabled"
          :disabled-time="disabledSecondLunchStartTime"
          :disabled-date="disabledSecondLunchStartDate"
          @change="handleChangeSecondLunchStart"
          @openChange="handleOpenChangeSecondLunchStart"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="End" prop="second_lunch_end">
        <a-date-picker
          v-model="shiftForm.second_lunch_end"
          placeholder="Select End"
          :show-time="{ format: 'h:mm a', use12Hours: true }"
          :format="dateTimeFormat"
          :disabled="isSecondLunchEndDisabled"
          :disabled-time="disabledSecondLunchEndTime"
          :disabled-date="disabledSecondLunchEndDate"
          @change="handleChangeSecondLunchEnd"
          @openChange="handleOpenChangeSecondLunchEnd"
        /> </a-form-model-item
      ><a-alert type="info" show-icon v-for="message in secondLunchWaiveInfo" :key="message" :message="message" />
      <a-form-model-item
        :colon="false"
        label-align="left"
        label="Waive Meal Break"
        :label-col="{ span: 20 }"
        :wrapper-col="{ span: 4 }"
      >
        <div class="flex justify-content-end">
          <a-switch v-model="isSecondLunchWaived" :disabled="!isSecondLunchWaiveAvailable || !isLunchWaiver" />
        </div>
      </a-form-model-item>

      <a-form-model-item
        v-if="showEmergencyOnCall"
        :colon="false"
        :label-col="{ span: 20 }"
        :wrapper-col="{ span: 4 }"
        label-align="left"
        label="Change Schedule"
      >
        <div class="flex justify-content-end">
          <a-switch v-model="shiftForm.set_scheduled_as_signed" :disabled="disableScheduledAsSigned" />
        </div>
      </a-form-model-item>
      <a-form-model-item
        v-if="showChangeSchedule"
        :colon="false"
        :label-col="{ span: 20 }"
        :wrapper-col="{ span: 4 }"
        label-align="left"
        label="Rest Break"
      >
        <div class="flex justify-content-end">
          <a-switch v-model="shiftForm.rest_break_checked" />
        </div>
      </a-form-model-item>
      <a-form-model-item
        :colon="false"
        :label-col="{ span: 20 }"
        :wrapper-col="{ span: 4 }"
        label-align="left"
        label="Emergency On-Call"
      >
        <div class="flex justify-content-end">
          <a-switch v-model="shiftForm.emergency_on_call" />
        </div>
      </a-form-model-item>
    </div>
  </a-form-model>
</template>

<script>
import { Alert, Button, DatePicker, FormModel, Switch } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import validation from '@/modules/shifts/mixins/ShiftValidationMixin'
import moment from 'moment-timezone'

const { mapActions: shiftActions, mapState: shiftState } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-button': Button,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-switch': Switch,
    'a-date-picker': DatePicker,
    'a-alert': Alert
  },
  mixins: [validation],
  props: {
    isNew: {
      type: Boolean,
      default: true
    },
    associate_number: {
      type: String,
      default: undefined
    },
    isLunchWaiver: {
      type: Boolean,
      default: false
    },
    disableScheduledAsSigned: {
      type: Boolean,
      default: false
    },
    valid: {
      type: Boolean,
      default: true
    },
    sourceShift: {
      type: Object,
      default: null
    },
    showChangeSchedule: {
      type: Boolean,
      default: true
    },
    showEmergencyOnCall: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLunchWaived: false,
      isSecondLunchWaived: false,

      changeByAutofill: false,

      rules: {
        work_start: [
          {
            validator: this.createValidator('validateWorkStart'),
            trigger: 'change',
            message: this.validateWorkStart
          }
        ],
        work_end: [
          {
            validator: this.createValidator('validateWorkEnd'),
            trigger: 'change',
            message: this.validateWorkEnd
          }
        ],
        lunch_start: [
          {
            validator: this.createValidator('validateFirstLunchStart'),
            trigger: 'change',
            message: this.validateFirstLunchStart
          }
        ],
        lunch_end: [
          {
            validator: this.createValidator('validateFirstLunchEnd'),
            trigger: 'change',
            message: this.validateFirstLunchEnd
          }
        ],
        second_lunch_start: [
          {
            validator: this.createValidator('validateSecondLunchStart'),
            trigger: 'change',
            message: this.validateSecondLunchStart
          }
        ],
        second_lunch_end: [
          {
            validator: this.createValidator('validateSecondLunchEnd'),
            trigger: 'change',
            message: this.validateSecondLunchEnd
          }
        ]
      }
    }
  },
  watch: {
    shiftForm: {
      handler(newForm) {
        for (const [field, value] of Object.entries(newForm)) {
          this.handleFieldChange(field, value)
        }
      },
      deep: true
    },
    'shiftForm.work_start': {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal || this.changeByAutofill) {
          this.changeByAutofill = false
          return
        }

        const diffInDays = newVal.diff(oldVal, 'days')

        if (diffInDays === 0) return

        const adjustDateByDays = (date, days) => {
          return moment(date).add(days, 'days')
        }

        if (
          diffInDays >= 1 &&
          this.disabledSignedStartDate(
            adjustDateByDays(this.shiftForm.work_end, diffInDays),
            !this.isNew && !this.shiftForm.set_scheduled_as_signed
          )
        ) {
          return
        }

        if (this.shiftForm.work_end) {
          this.shiftForm.work_end = adjustDateByDays(this.shiftForm.work_end, diffInDays)
        }

        if (this.shiftForm.lunch_start) {
          this.shiftForm.lunch_start = adjustDateByDays(this.shiftForm.lunch_start, diffInDays)
        }

        if (this.shiftForm.lunch_end) {
          this.shiftForm.lunch_end = adjustDateByDays(this.shiftForm.lunch_end, diffInDays)
        }

        if (this.shiftForm.second_lunch_start) {
          this.shiftForm.second_lunch_start = adjustDateByDays(this.shiftForm.second_lunch_start, diffInDays)
        }

        if (this.shiftForm.second_lunch_end) {
          this.shiftForm.second_lunch_end = adjustDateByDays(this.shiftForm.second_lunch_end, diffInDays)
        }

        this.validateFields()
      }
    },
    'shiftForm.set_scheduled_as_signed': {
      async handler(newVal) {
        if (newVal) {
          this.shiftForm.scheduled_start = this.shiftForm.work_start
          this.shiftForm.scheduled_end = this.shiftForm.work_end
          this.shiftForm.scheduled_lunch_start = this.shiftForm.lunch_start
          this.shiftForm.scheduled_lunch_end = this.shiftForm.lunch_end
          this.shiftForm.scheduled_second_lunch_start = this.shiftForm.second_lunch_start
          this.shiftForm.scheduled_second_lunch_end = this.shiftForm.second_lunch_end

          this.shiftForm.hours = {
            ...this.shiftForm.hours,
            scheduled_work_hours: this.shiftForm.hours.work_hours,
            scheduled_pay_hours: this.shiftForm.hours.pay_hours,
            scheduled_duration: this.shiftForm.hours.duration
          }
        } else {
          Object.assign(this.shiftForm, this.shift)
        }
      }
    },
    async isLunchWaived(newVal) {
      this.clearLunch()
      this.handleFieldChange('lunch_waived', newVal ?? false)
      await this.fillHours()
    },
    async isSecondLunchWaived(newVal) {
      this.clearSecondLunch()
      this.handleFieldChange('lunch_waived', newVal ?? false)
      await this.fillHours()
    }
  },
  computed: {
    ...shiftState({
      storedShiftForm: state => state.shiftForm,
      storedShift: state => state.shift.shift
    }),
    shiftForm() {
      return this.storedShiftForm
    },
    shift() {
      return this.sourceShift ?? this.storedShift
    }
  },

  methods: {
    ...shiftActions(['updateShiftField', 'getEstimatedLunches']),

    setChangeByAutofill(value) {
      this.changeByAutofill = value
    },

    createValidator(validateFn) {
      return (rule, value, callback) => {
        const error = this[validateFn]
        if (error) {
          callback(new Error(error))
        } else {
          callback()
        }
      }
    },

    validateFields() {
      if (this.$refs.shiftFormRef) {
        this.$refs.shiftFormRef
          .validate()
          .then(() => {})
          .catch(errors => {})
      }
    },

    async validateForm() {
      const valid = await new Promise(resolve => {
        this.$refs.shiftFormRef.validate(resolve)
      })

      this.$emit('update:valid', valid)
    },

    handleFieldChange(field, value) {
      this.updateShiftField({ field, value })
    },

    async handleChangeSignedStart(date) {
      if (!date) this.clearSignedTime()
      else {
        this.shiftForm.work_start = this.resetSeconds(this.shiftForm.work_start)

        if (!this.shiftForm.work_end) {
          this.shiftForm.work_end = this.workEndInitialTime
        }

        await this.saveEstimatedTimes()

        this.validateFields()
      }
    },

    async handleChangeSignedEnd(date) {
      if (!date) this.clearLunch()
      else this.shiftForm.work_end = this.workEndInitialTime

      await this.saveEstimatedTimes()

      this.validateFields()
    },

    handleOpenChangeLunchStart() {
      this.shiftForm.lunch_start = this.lunchStartInitialTime

      if (!this.shiftForm.lunch_end) {
        this.shiftForm.lunch_end = this.lunchEndInitialTime
      }
    },

    async handleChangeLunchStart(date) {
      if (!date) this.clearLunch()
      else this.handleOpenChangeLunchStart()

      this.validateFields()
    },

    handleOpenChangeLunchEnd() {
      this.shiftForm.lunch_end = this.lunchEndInitialTime
    },

    async handleChangeLunchEnd(date) {
      if (!date) this.clearSecondLunch()
      else this.handleOpenChangeLunchEnd()

      this.validateFields()
    },

    handleOpenChangeSecondLunchStart() {
      this.shiftForm.second_lunch_start = this.secondLunchStartInitialTime

      if (!this.shiftForm.second_lunch_end) {
        this.shiftForm.second_lunch_end = this.secondLunchEndInitialTime
      }
    },

    async handleChangeSecondLunchStart(date) {
      if (!date) this.clearSecondLunch()
      else this.handleOpenChangeSecondLunchStart()

      this.validateFields()
    },

    handleOpenChangeSecondLunchEnd() {
      this.shiftForm.second_lunch_end = this.secondLunchEndInitialTime
    },

    async handleChangeSecondLunchEnd(date) {
      if (!date) return
      else this.handleOpenChangeSecondLunchEnd()

      this.validateFields()
    },

    // clear fields

    clearSignedTime() {
      this.shiftForm.work_start = null
      this.shiftForm.work_end = null

      this.$refs.shiftFormRef.clearValidate('work_start')
      this.$refs.shiftFormRef.clearValidate('work_end')

      this.clearLunch()
      this.clearSecondLunch()
      this.validateFields()
    },

    clearLunch() {
      this.shiftForm.lunch_start = null
      this.shiftForm.lunch_end = null

      this.$refs.shiftFormRef.clearValidate('lunch_start')
      this.$refs.shiftFormRef.clearValidate('lunch_end')

      this.clearSecondLunch()
      this.validateFields()
    },

    clearSecondLunch() {
      this.shiftForm.second_lunch_start = null
      this.shiftForm.second_lunch_end = null

      this.$refs.shiftFormRef.clearValidate('second_lunch_start')
      this.$refs.shiftFormRef.clearValidate('second_lunch_end')
      this.validateFields()
    },

    fillLunchWaived() {
      this.isLunchWaived = this.isLunchWaiveAvailable && !!this.shiftForm.lunch_waived && !this.shiftForm.lunch_start

      this.isSecondLunchWaived =
        this.isSecondLunchWaiveAvailable && !!this.shiftForm.lunch_waived && !this.shiftForm.second_lunch_start
    },

    // autofill

    async saveEstimatedTimes() {
      const estimatedLunches = (await this.fillLunches()) ?? {}
      const { estimated_lunch_end, estimated_lunch_start, estimated_second_lunch_end, estimated_second_lunch_start } =
        estimatedLunches

      await this.fillHours()

      this.estimated_lunch_end = estimated_lunch_end
      this.estimated_lunch_start = estimated_lunch_start
      this.estimated_second_lunch_end = estimated_second_lunch_end
      this.estimated_second_lunch_start = estimated_second_lunch_start
    },

    async fillWorkTimes() {
      this.shiftForm.rest_break_checked = true
      this.changeByAutofill = true

      const estimatedWorkStart = this.shiftForm.scheduled_start
        ? moment(this.shiftForm.scheduled_start)
        : this.shiftForm.work_start
          ? moment(this.shiftForm.work_start)
          : moment()
      const estimatedWorkEnd = this.shiftForm.scheduled_end
        ? moment(this.shiftForm.scheduled_end)
        : this.shiftForm.work_end
          ? moment(this.shiftForm.work_end)
          : moment().add(510, 'minutes')

      let workStart = this.shiftForm.work_start ? moment(this.shiftForm.work_start) : estimatedWorkStart.clone()
      let workEnd = this.shiftForm.work_end ? moment(this.shiftForm.work_end) : estimatedWorkEnd.clone()

      if (estimatedWorkStart.isAfter(workStart)) {
        const diff = Math.abs(workStart.diff(estimatedWorkStart, 'minutes'))
        const needChange = diff > this.beforeShiftStartGracePeriod
        workStart = needChange ? estimatedWorkStart.clone() : workStart
      } else if (estimatedWorkStart.isBefore(workStart)) {
        const diff = Math.abs(workStart.diff(estimatedWorkStart, 'minutes'))
        const needChange = diff > this.afterShiftStartGracePeriod
        workStart = needChange ? estimatedWorkStart.clone() : workStart
      }

      if (estimatedWorkEnd.isAfter(workEnd)) {
        const diff = Math.abs(workEnd.diff(estimatedWorkEnd, 'minutes'))
        const needChange = diff > this.beforeShiftEndGracePeriod
        workEnd = needChange ? estimatedWorkEnd.clone() : workEnd
      } else if (estimatedWorkEnd.isBefore(workEnd)) {
        const diff = Math.abs(workEnd.diff(estimatedWorkEnd, 'minutes'))
        const needChange = diff > this.afterShiftEndGracePeriod
        workEnd = needChange ? estimatedWorkEnd.clone() : workEnd
      }

      this.shiftForm.work_start = workStart
      this.shiftForm.work_end = workEnd

      await this.saveEstimatedTimes()

      if (!this.isValidFirstLunchStart) {
        this.shiftForm.lunch_start = this.estimated_lunch_start
        this.shiftForm.lunch_end = this.estimated_lunch_end
      }

      if (!this.isValidSecondLunchStart) {
        this.shiftForm.second_lunch_start = this.estimated_second_lunch_start
        this.shiftForm.second_lunch_end = this.estimated_second_lunch_end
      }

      this.validateFields()
    },

    async fillLunches() {
      try {
        const estimatedLunches = await this.getEstimatedLunches({
          associate_number: this.associate_number,
          shift_start: this.shiftForm.work_start,
          shift_end: this.shiftForm.work_end,
          lunch_waived: false,
          project_id: this.$store.state.applicationState.currentProject.id
        })

        return estimatedLunches
      } catch (error) {
        console.error(error)
        return {}
      }
    }
  },
  mounted() {
    this.fillLunchWaived()
  }
}
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 0;
}

.ant-calendar-picker {
  width: 100%;
}

h3 {
  margin: 0;
}
</style>
